import { styled, css } from '@lichtblick/styled';
import { BorderRadius, Colors } from '@lichtblick/theme';
import { ButtonSize, ButtonVariant } from '@lichtblick/types';

export const BaseButton = styled.button<{
  $isLoading?: boolean;
  $isParentSize?: boolean;
  $size: ButtonSize;
  $variant: ButtonVariant;
}>`
  align-items: center;
  cursor: pointer;
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;
  width: ${({ $isParentSize }) => $isParentSize && '100%'};
  ${({ $variant }) => {
    switch ($variant) {
      case 'primary':
        return css`
          background-color: ${Colors.blue[100]};
          border: none;
          color: ${Colors.white};

          &:hover {
            background-color: ${Colors.darkBlue};
          }

          &:disabled {
            background-color: ${Colors.blue[60]};
            color: ${Colors.white};
          }
        `;
      case 'ghost-primary':
        return css`
          background-color: transparent;
          border: none;
          font-weight: 500;
          color: ${Colors.blue[100]};

          &:disabled {
            color: ${Colors.blue[60]};
          }
        `;
      case 'secondary':
        return css`
          background-color: ${Colors.white};
          border: 1px solid ${Colors.blue[20]};
          color: ${Colors.blue[100]};

          &:hover {
            background-color: ${Colors.blue[5]};
          }

          &:disabled {
            background-color: ${Colors.white};
            border-color: ${Colors.blue[20]};
            color: ${Colors.blue[60]};
          }
        `;
      case 'approval':
        return css`
          background-color: ${Colors.green[100]};
          border: none;
          color: ${Colors.white};

          &:hover {
            background-color: ${Colors.green[100]};
          }

          &:disabled {
            background-color: ${Colors.blue[20]};
            color: ${Colors.blue[60]};
          }
        `;
      case 'rejection':
        return css`
          background-color: ${Colors.red[100]};
          border: none;
          color: ${Colors.white};

          &:hover {
            background-color: ${Colors.red[100]};
          }

          &:disabled {
            background-color: ${Colors.blue[20]};
            color: ${Colors.blue[60]};
          }
        `;
      case 'icon':
        return css`
          background-color: ${Colors.white};
          border: none;
          color: ${Colors.blue[100]};

          &:hover {
            background-color: ${Colors.blue[5]};
          }

          &:disabled {
            background-color: ${Colors.blue[5]};
            color: ${Colors.blue[60]};
          }
        `;
    }
  }};

  ${({ $size }) => {
    switch ($size) {
      case 'large':
        return css`
          border-radius: ${BorderRadius.Xs};
          height: 2.5rem;
          padding: 0rem 1rem;
          svg {
            height: 1.5rem;
            width: 1.5rem;
          }
        `;
      case 'small':
        return css`
          border-radius: ${BorderRadius.Xxs};
          height: 2rem;
          padding: 0rem 0.75rem;
          svg {
            height: 1rem;
            width: 1rem;
          }
        `;
    }
  }};

  ${({ $isLoading, $variant }) =>
    $isLoading
      ? css`
          background-color: ${Colors.blue[60]};
          border-color: ${$variant === 'ghost-primary' ? 'transparent' : Colors.blue[60]};
          color: ${Colors.white};
          cursor: wait;
        `
      : null}

  ${({ $isParentSize }) =>
    $isParentSize
      ? css`
          width: 100%;
        `
      : null}
`;

export const Spinner = styled.span`
  animation: spin 0.8s linear infinite;
  border: 2px solid currentColor;
  border-radius: 9999px;
  border-top-color: transparent;
  display: inline-block;
  height: 1rem;
  width: 1rem;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
